import Config from '@config';
import { normalizeInfiniteQueryData } from '@gasbi/shared/common/features/utils';
import { createApi } from '@reduxjs/toolkit/query/react';

import { CACHE_EXPIRATION_TIME } from '@/features/constants';
import { FiltersGrid, Pagination } from '@/features/types';
import axiosBaseQuery from '@/utils/axiosBaseQuery';
import { buildGridifySearchParams } from '@/utils/buildGridifySearchParams';

import { infiniteQueryOptions } from '../utils';
import {
  Subsystem,
  SubsystemDocumentType,
  SubsystemGrid,
  SubsystemRequestBody,
} from './types';

const subsystemsApi = createApi({
  reducerPath: 'subsystemsApi',
  baseQuery: axiosBaseQuery({
    baseUrl: `${Config.VITE_API_URL}/subsystems`,
  }),
  tagTypes: ['Subsystem', 'Subsystems', 'SubsystemDocumentType'],
  keepUnusedDataFor: CACHE_EXPIRATION_TIME,
  endpoints: builder => ({
    getSubsystemsI: builder.infiniteQuery<
      SubsystemGrid,
      FiltersGrid & { documentTypeIds: string[] },
      Pagination
    >({
      infiniteQueryOptions,
      query: ({ queryArg, pageParam }) => {
        const queryParams = buildGridifySearchParams({
          ...queryArg,
          ...pageParam,
        });

        return {
          url: `/query?${queryParams}`,
        };
      },
      providesTags: () => ['Subsystems'],
    }),
    getSubsystemsById: builder.query<Subsystem, string>({
      query: id => ({
        url: `/${id}`,
      }),
      providesTags: () => ['Subsystem'],
    }),
    createSubsystem: builder.mutation<Subsystem, SubsystemRequestBody>({
      query: data => ({
        url: '/',
        method: 'POST',
        data,
      }),
      invalidatesTags: result => [
        'Subsystems',
        { type: 'Subsystem', id: result?.id },
      ],
    }),
    updateSubsystems: builder.mutation<Subsystem, SubsystemRequestBody>({
      query: data => ({
        url: `/${data.id}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: result => [
        'Subsystems',
        { type: 'Subsystem', id: result?.id },
      ],
    }),
    getSubsystemDocumentTypeI: builder.infiniteQuery<
      SubsystemDocumentType,
      FiltersGrid,
      Pagination
    >({
      infiniteQueryOptions,
      query: ({ queryArg, pageParam }) => ({
        url: '/document-types/query',
        params: { ...queryArg, ...pageParam },
      }),
      providesTags: () => ['SubsystemDocumentType'],
    }),
  }),
});

export default subsystemsApi;

export const {
  useGetSubsystemsByIdQuery,
  useCreateSubsystemMutation,
  useUpdateSubsystemsMutation,
} = subsystemsApi;

export const useGetSubsystemsIInfiniteQuery = normalizeInfiniteQueryData(
  subsystemsApi.useGetSubsystemsIInfiniteQuery,
);

export const useGetSubsystemDocumentTypeIInfiniteQuery =
  normalizeInfiniteQueryData(
    subsystemsApi.useGetSubsystemDocumentTypeIInfiniteQuery,
  );
